export function useLocationFormatted(location, error) {
  const unitSettings = useSafeLocalStorage("unit-settings", {
    elevationUnits: "ft",
    speedUnits: "mph",
  });

  const elevationUnits = computed(() => {
    return ELEVATION_UNITS[unitSettings.value?.elevationUnits] ?? ELEVATION_UNITS.ft;
  });

  const speedUnits = computed(() => {
    return SPEED_UNITS[unitSettings.value?.speedUnits] ?? SPEED_UNITS.mph;
  });

  const elevation = computed(() => {
    return isNumber(location.value?.altitude)
      ? numberFormatted(location.value?.altitude * elevationUnits.value.multiplier)
      : null;
  });

  const percentOfMtEverest = computed(() => {
    const HEIGHT_OF_EVEREST = 8848.86;
    return isNumber(location.value?.altitude)
      ? numberFormatted((location.value?.altitude / HEIGHT_OF_EVEREST) * 100, {
          decimals: 2,
          avoidZero: true,
        }) + "%"
      : null;
  });

  const boilingPoint = computed(() => {
    if (!isNumber(location.value?.altitude)) return null;

    const units = unitSettings.value.elevationUnits === "ft" ? "imperial" : "metric";
    const boilingPoint = numberFormatted(boilingPointForElevation(location.value?.altitude, units));
    return {
      value: boilingPoint,
      units: units === "imperial" ? "°F" : "°C",
    };
  });

  const accuracy = computed(() => {
    return isNumber(location.value?.altitudeAccuracy)
      ? numberFormatted(location.value?.altitudeAccuracy * elevationUnits.value.multiplier)
      : null;
  });

  const speed = computed(() => {
    if (!isNumber(location.value?.speed)) {
      return null;
    }
    const speed = numberFormatted(location.value?.speed * speedUnits.value.multiplier, {
      decimals: speedUnits.value.decimals || 0,
      avoidZero: true,
    });
    if (speedUnits.value.name === SPEED_UNITS.mach.name) {
      return `${speedUnits.value.longName} ${speed}`;
    }
    return `${speed} ${speedUnits.value.name}`;
  });

  const coordinates = computed(() => {
    return isNumber(location.value?.latitude)
      ? `${round(location.value?.latitude, 4)}, ${round(location.value?.longitude, 4)}`
      : null;
  });

  const direction = computed(() => {
    return isNumber(location.value?.heading) ? headingDirectionFormatted(location.value?.heading) : null;
  });

  const heading = computed(() => {
    return isNumber(location.value?.heading) ? `${round(location.value?.heading)}°` : null;
  });

  const errorMessage = computed(() => {
    // handle GeolocationPositionError
    if (isGeolocationError(error)) {
      switch (error.value.code) {
        case 1: // PERMISSION_DENIED
          return "Location access denied. Enable location access or search for a location to continue.";
        case 2: // POSITION_UNAVAILABLE
        case 3: // TIMEOUT
          return "Your device was unable to find your location. Please try again";
      }
    }
    return null;
  });

  const elevationSource = computed(() => {
    if (!isNumber(location.value?.altitude)) return null;

    // web readings are described by the ElevationSource component
    return location.value?.altitudeSource === "web" ? null : "According to your device.";
  });

  return {
    unitSettings,
    elevationUnits,
    speedUnits,
    elevation,
    percentOfMtEverest,
    boilingPoint,
    speed,
    accuracy,
    elevationSource,
    coordinates,
    direction,
    heading,
    errorMessage,
  };
}
